import { getAuthData } from '../../Utils/AuthUtils';
import apiService from '../../Services/APIService';
import { checkAPIResponse } from '../../Utils/APIResponseUtils';
import { getFromIndexedDB } from '../../Utils/IndexedDBUtils';
import { format } from 'date-fns';
import store from '../../Store/Store';

const authData = getAuthData();
const currentUserID = authData?.UserID ? String(authData.UserID) : "";

export const manageConnectionAPI = async (connectionData, operation = 'ADD') => {
  const state = store.getState();
  try {
    const d = connectionData;

    // Handle COLD toggle operation
    if (operation === 'COLD' || (d.ConnID && d.IsCold !== undefined && !d.name && !d.businessName)) {
      const preparedContactData = {
        changeType: 'COLD',
        connID: d.ConnID || d.connID || 0,
        isCold: d.IsCold || 0,
        pocUserID: currentUserID,
        dueDT: null,
        recDT: null,
        recType: "",
        recUID: currentUserID,
        recNotes: "",
        recLat: "",
        recLon: "",
        recThreadIDs: "",
        taskType: "",
        taskName: "",
        taskThreadIDs: "",
        nextTaskDT: null,
        nextTaskTyp: "",
        nextTaskPOCUID: "0",
        nextTaskNotes: "",
        contacts: "",
        nextTaskThreads: "",
        description: "",
        taskID: 0,
        priority: "",
        status: 0,
        taskContractIDs: "",
        recContractIDs: "",
        currStageID: 0,
        reason: "",
        recSecConnIDs: "",
        nextTaskSecConnIDs: "",
        recID: 0,
        rmdrDT: null
      };

      const result = await apiService.sendChange(2, 'action', preparedContactData, { isWeb: true });
      checkAPIResponse(result);
      return result;
    }

    const isDelete = operation === 'DELETE';

    // Process customized fields - this is the key fix
    let customizedFieldsValue = '[]'; // Default empty array as string

    if (d.customizedFields && typeof d.customizedFields === 'string') {
      // If it's already a string, use it as is
      customizedFieldsValue = d.customizedFields;
    } else if (d.customizedFields && Array.isArray(d.customizedFields)) {
      // If it's an array, stringify it
      customizedFieldsValue = JSON.stringify(d.customizedFields);
    } else if (d.customizedFields && typeof d.customizedFields === 'object') {
      // If it's an object (the extra fields data), convert to the expected format
      const customFieldsArray = [];

      if (d.extraFieldsData && Array.isArray(d.extraFieldsData)) {
        d.extraFieldsData.forEach(field => {
          if (field.value && field.value.trim() !== '') {
            customFieldsArray.push({
              FID: field.FID,
              F: field.fieldName,
              V: field.value
            });
          }
        });
      }
      customizedFieldsValue = JSON.stringify(customFieldsArray);
    }

    // Helper to ensure multi-ID fields (like pocUserID) remain as a comma-separated string
    const toMultiId = (val, def = "0") => {
      if (val === null || val === undefined || String(val).trim() === "") return def;
      return Array.isArray(val) ? val.join(',') : String(val).trim();
    };

    // Helper to ensure single-ID fields (like nextTaskPOCUserID) take only the first ID
    const toSingleId = (val, def = "0") => {
      if (val === null || val === undefined || String(val).trim() === "") return def;
      const s = Array.isArray(val) ? String(val[0]) : String(val);
      return s.split(',')[0].trim() || def;
    };

    // Helper to safely format dates to 'dd-MMM-yy' for SQL Server smalldatetime compatibility
    const formatDateSafe = (dateVal, formatStr = 'dd-MMM-yy') => {
      if (!dateVal) return null;
      try {
        if (typeof dateVal === 'string') {
          if (/^\d{1,2}-[A-Za-z]{3}-\d{2,4}$/.test(dateVal.trim())) {
            return dateVal.trim();
          }
          const parsedDate = new Date(dateVal);
          if (!isNaN(parsedDate.getTime())) {
            return format(parsedDate, formatStr);
          }
          return dateVal;
        }
        if (dateVal instanceof Date && !isNaN(dateVal.getTime())) {
          return format(dateVal, formatStr);
        }
        return dateVal;
      } catch (e) {
        return dateVal;
      }
    };

    const preparedConnectionData = {
      changeType: isDelete ? 'DEL' : operation,
      connID: toSingleId(d.connID),
      name: d.name || "",
      businessName: d.businessName || "",
      designation: d.designation || "",
      cc: toSingleId(d.cc, "91"),
      mobile: d.mobile || "",
      email: d.email || "",
      notes: d.notes || "",
      gstin: d.gstin || "",
      city: d.city || "",
      state: d.state || "",
      stateID: toSingleId(d.stateID),
      country: d.country || "",
      countryID: toSingleId(d.countryID, "1"),
      pocUserID: toMultiId(d.pocUserID || currentUserID),
      nextTaskDT: isDelete
        ? (d.NextTaskDT || null)
        : (d.nextTaskDT ? `${formatDateSafe(d.nextTaskDT, 'dd-MMM-yy')} ${d.nextTaskTime || '11:00'}` : null),
      nextTaskTyp: d.nextTaskTyp || "",
      nextTaskNotes: d.nextTaskNotes || "",
      nextTaskPOCUserID: toSingleId(d.nextTaskPocUserID || (Array.isArray(d.pocUserID) ? d.pocUserID[0] : d.pocUserID) || currentUserID),
      source: d.source || "",
      sinceDT: d.since ? formatDateSafe(d.since, 'dd-MMM-yy') : (d.thrProduct ? format(new Date(), 'dd-MMM-yy') : null),
      segment: d.segment || "",
      tags: d.tags || (isDelete ? [] : ""),
      potential: toSingleId(d.businessPotential),
      connType: d.connType || "",
      customizedFields: customizedFieldsValue,
      contacts: d.contacts ? JSON.stringify(d.contacts) : '[]',
      recUID: toSingleId(d.interactionBy || currentUserID),
      recType: d.recordType || null,
      recDT: isDelete
        ? null
        : (d.recordDate && d.recordTime ? `${formatDateSafe(d.recordDate, 'dd-MMM-yy')} ${d.recordTime}` : null),
      recNotes: d.recordNotes || null,
      recLat: d.recLat || "",
      recLon: d.recLon || "",
      isSendEmail: d.isSendEmail || false,
      regionID: toSingleId(d.regionID),
      thrProduct: d.thrProduct || "",
      thrModel: d.thrModel || "",
      thrQty: d.thrQty || "",
      thrPotential: toSingleId(d.thrPotential),
      thrDueDT: d.thrDueDT ? formatDateSafe(d.thrDueDT, 'dd-MMM-yy') : null,
      thrNotes: d.thrNotes || "",
      thrStageID: toSingleId(d.thrStageID, "0"),
      threadID: toSingleId(d.threadID),
      reference: d.reference || "",
      thrCustomFields: d.thrCustomFields ? JSON.stringify(d.thrCustomFields) : "",
      attachment: d.attachment || ""
    };

    const result = await apiService.sendChange(2, 'set', preparedConnectionData);
    checkAPIResponse(result);

    let extractedConnID = d.connID || 0;

    if (operation === 'ADD' && result && result.updatePackage) {
      try {
        const updatePackage = JSON.parse(result.updatePackage);
        const tableA = updatePackage.Table && updatePackage.Table.length > 0 ? updatePackage.Table[0] : null;

        if (tableA && tableA.RetData) {
          const retData = JSON.parse(tableA.RetData);
          if (retData && retData.length > 0 && retData[0].ConnID) {
            extractedConnID = retData[0].ConnID;
          }
        }
      } catch (error) {
        console.error('Error parsing API response for ConnID:', error);
        // Fallback to using the original connID if parsing fails
      }
    }

    return {
      ...result,
      connID: extractedConnID,
      connectionData: {
        ConnID: extractedConnID,
        connID: extractedConnID,
        BizName: d.businessName,
        ContactName: d.name,
        name: d.name
      }
    };

  } catch (error) {
    console.error(`${operation} contact API error:`, error);
    throw error;
  }
};


export const manageOtherContactsAPI = async (connID, contacts, pocUserID = '0') => {
  try {
    const authData = getAuthData();
    const currentUserID = authData?.UserID ? String(authData.UserID) : "0";

    // Generate unique CIDs starting from 1, ensuring no duplicates
    const formattedContacts = contacts.map((contact, index) => ({
      CID: index + 1, // Always use sequential CIDs starting from 1
      CNm: contact.name || contact.CNm || '',
      MNo: contact.mobile || contact.MNo || '',
      Eml: contact.email || contact.Eml || '',
      Dsgn: contact.designation || contact.Dsgn || '',
      CC: contact?.countryCode || '91'
    }));
    const preparedContactData = {
      // Original first JSON properties
      changeType: 'OCNTCADD',
      connID: connID || 0,
      pocUserID: pocUserID || "0",
      dueDT: null,
      recDT: null,
      recType: "",
      recUID: currentUserID,
      recNotes: "",
      recLat: "",
      recLon: "",
      recThreadIDs: "",
      taskType: "",
      taskName: "",
      taskThreadIDs: "",
      nextTaskDT: null,
      nextTaskTyp: "",
      nextTaskPOCUID: "0",
      nextTaskNotes: "",
      contacts: JSON.stringify(formattedContacts),
      nextTaskThreads: "",
      description: "",
      taskID: 0,
      priority: "",
      status: 0,
      taskContractIDs: "",
      recContractIDs: "",
      currStageID: 0,
      reason: '',
      recSecConnIDs: '',
      nextTaskSecConnIDs: '',
      recID: 0,
      rmdrDT: null,
      isCold: 0
    };
    const result = await apiService.sendChange(2, 'action', preparedContactData, { isWeb: true });
    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error('Manage other contacts API error:', error);
    throw error;
  }
};

export const getConnectionDetailAPI = async (connID, getType = '') => {
  try {
    let apiOptions = { isWeb: true };
    if (connID) {
      try {
        const conns = await getFromIndexedDB('Conns') || [];
        const connection = conns.find(c => Number(c.ConnID || c.NodeID || 0) === Number(connID));
        if (connection?.AgencyID) {
          const rawMyAgencies = localStorage.getItem('MyAgencies');
          if (rawMyAgencies) {
            const myAgencies = JSON.parse(rawMyAgencies);
            const matched = myAgencies.find(agn => String(agn.AgencyID) === String(connection.AgencyID));
            if (matched) {
              apiOptions.userID = Number(matched.UserID);
              apiOptions.forAgencyID = Number(matched.AgencyID);
            }
          }
        }
      } catch (e) {
        console.error('Error fetching connection for agency context in getConnectionDetailAPI:', e);
      }
    }
    const result = await apiService.getData(2, 'getDetails', { connID, getType }, apiOptions);

    // Parse the updatePackage to get the actual data
    const updatePackage = JSON.parse(result.updatePackage);
    const data = updatePackage.Table1[0];
    const transactionData = updatePackage.Table2 || []; // Get transactions from Table2
    const ordersHistoryData = updatePackage.Table4 || []; // Get orders history from Table4

    if (getType === 'TASKS') {
      // For tasks, look for the Tasks field and parse it
      const tasksData = data.Tasks ? JSON.parse(data.Tasks) : [];
      return Array.isArray(tasksData) ? tasksData : [];
    } else {
      // Default behavior - return records, transactions and other data
      const records = data.Records ? JSON.parse(data.Records) : [];

      return {
        records: Array.isArray(records) ? records : [],
        transactions: Array.isArray(transactionData) ? transactionData : [],
        openThreadIDs: data.OpenThreadIDs || '',
        tasks: data.Tasks ? JSON.parse(data.Tasks) : [],
        rawData: data,
        ordersHistory: Array.isArray(ordersHistoryData) ? ordersHistoryData : []
      };
    }
  } catch (error) {
    console.error('getConnectionDetail API error:', error);
    throw error;
  }
};
export const getConnectionWithActions = async (connID) => {
  try {
    const [actsData, connsData] = await Promise.all([
      getFromIndexedDB('Threads'),
      getFromIndexedDB('Conns')
    ]);

    const connection = (connsData || []).find(conn => conn.ConnID == connID);
    if (!connection) {
      return { data: null, loading: false, error: 'Contact not found' };
    }

    return {
      data: { ...connection, threadData: (actsData || []).filter(act => act.ConnID == connID) },
      loading: false,
      error: null
    };
  } catch (error) {
    console.error('Error fetching contact with actions:', error);
    return { data: null, loading: false, error: error.message };
  }
};

export const updateConnFieldPrefsAPI = async (preferences) => {
  try {
    const result = await apiService.sendChange(3, 'set', {
      changeType: "ENTRYPREFS",
      key: "CONN",
      value: "",
      values: JSON.stringify([preferences])
    }, { isWeb: true });

    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error('Failed to update contact field preferences:', error);
    throw error;
  }
};
export const updateAllFieldPrefsAPI = async (connPreferences, threadPreferences, tid) => {
  try {
    const payload = {
      ConnPrefs: [connPreferences],
      ThrPrefs: {
        TID: tid,
        Prefs: JSON.stringify(threadPreferences)
      }
    };

    const result = await apiService.sendChange(3, 'set', {
      changeType: "ENTRYPREFS",
      key: "CONNTHR",
      value: "",
      values: JSON.stringify(payload)
    }, { isWeb: true });

    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error('Failed to update field preferences:', error);
    throw error;
  }
};
export const manageConnectionAccessAPI = async (connID, changeType, userID) => {
  try {
    const preparedData = {
      changeType: changeType, // 'ACCADD' or 'ACCRM'
      connID: connID,
      pocUserID: userID,
      // Default empty/null fields required by the SP
      dueDT: null,
      recDT: null,
      recType: "",
      recUID: 0,
      recNotes: "",
      recLat: "",
      recLon: "",
      recThreadIDs: "",
      taskType: "",
      taskName: "",
      taskThreadIDs: "",
      nextTaskDT: null,
      nextTaskTyp: "",
      nextTaskPOCUID: "0",
      nextTaskNotes: "",
      contacts: "",
      nextTaskThreads: "",
      description: "",
      taskID: 0,
      priority: "",
      status: 0,
      taskContractIDs: "",
      recContractIDs: "",
      currStageID: 0,
      reason: "",
      recSecConnIDs: "",
      nextTaskSecConnIDs: "",
      recID: 0,
      isCold: 0,
      rmdrDT: null
    };

    const result = await apiService.sendChange(2, 'action', preparedData, { isWeb: true });
    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error('Connection Access API error:', error);
    throw error;
  }
};

// Function to save multiple connections in bulk
export const saveConnectionsBulk = async (connParams) => {
  try {

    if (!authData) {
      throw new Error('Authentication data not found');
    }

    // Make API call
    const result = await apiService.sendChangeForm(2, 'setBulk', connParams);

    // Validate response
    checkAPIResponse(result);
    if (result.error) {
      throw new Error(result.message || 'API request failed');
    }

    return { success: true, message: 'All contacts saved.', result };
  } catch (error) {
    throw error;
  }
};

export const getConnectionTasksAPI = async (connID) => {
  try {
    const result = await apiService.getData(2, 'getDetails', { connID, getType: 'TASKS' }, { isWeb: true });

    // Parse the updatePackage to get the actual data
    const updatePackage = JSON.parse(result.updatePackage);
    const data = updatePackage.Table1[0];

    // Handle the Tasks field and parse it
    const tasksData = data.Tasks ? JSON.parse(data.Tasks) : [];
    return Array.isArray(tasksData) ? tasksData : [];
  } catch (error) {
    console.error('getConnectionTasks API error:', error);
    // Return empty array instead of throwing error
    return [];
  }
};

// Function which will manage feedback request API
export const manageFdbkRequestAPI = async (fdbkReqData) => {
  const state = store.getState();
  try {
    fdbkReqData = [{
      ...fdbkReqData,
      changeType: 'FDBKREQ',
      agencyID: state.agencyData.data.AgencyID,
      agencyName: state.agencyData.data.AgencyName,
      dueDT: null,
      recDT: null,
      recType: "",
      recUID: "",
      recNotes: "",
      recLat: "",
      recLon: "",
      recThreadIDs: "",
      taskType: "",
      taskName: "",
      taskThreadIDs: "",
      nextTaskDT: null,
      nextTaskTyp: "",
      nextTaskPOCUID: "0",
      nextTaskNotes: "",
      contacts: "",
      nextTaskThreads: "",
      description: "",
      taskID: 0,
      priority: "",
      status: 0,
      taskContractIDs: "",
      recContractIDs: "",
      currStageID: 1,
      reason: "",
      recSecConnIDs: "",
      taskSecConnIDs: "",
    }]
    const result = await apiService.sendChange(2, 'requestFeedback', fdbkReqData);
    return result;
  } catch (error) {
    console.error(`Feedback request API error:`, error);
    throw error;
  }
};
